/**
 * modes.ts — loads media/modes.json at runtime and validates structure.
 *
 * Phase 1 (media-prep) produces /media/modes.json. This module is the single entry
 * point every later phase uses to discover available modes. Fails loudly with a
 * descriptive Error if the file is missing or shape-invalid; never returns partial.
 *
 * Never imports Tone, three, or any rendering layer.
 */

import type { ModesFile, ModeConfig } from './types';

const DEFAULT_MODES_URL = '/media/modes.json';

export async function loadModes(url: string = DEFAULT_MODES_URL): Promise<ModesFile> {
  const res = await fetch(url);
  if (!res.ok) {
    throw new Error(`loadModes: fetch ${url} failed with ${res.status}`);
  }
  const raw: unknown = await res.json();
  return assertModesFile(raw, url);
}

function assertModesFile(raw: unknown, source: string): ModesFile {
  if (!raw || typeof raw !== 'object' || !('modes' in raw)) {
    throw new Error(`loadModes: ${source} missing top-level "modes" array`);
  }
  const modes = (raw as { modes: unknown }).modes;
  if (!Array.isArray(modes) || modes.length === 0) {
    throw new Error(`loadModes: ${source} "modes" must be a non-empty array`);
  }
  modes.forEach((m, i) => assertModeConfig(m, i, source));
  return { modes: modes as ModeConfig[] };
}

function assertModeConfig(raw: unknown, index: number, source: string): void {
  if (!raw || typeof raw !== 'object') {
    throw new Error(`loadModes: ${source} modes[${index}] not an object`);
  }
  const m = raw as Record<string, unknown>;
  const requiredStrings = ['id', 'name', 'video', 'bed', 'key'];
  for (const k of requiredStrings) {
    if (typeof m[k] !== 'string') {
      throw new Error(`loadModes: ${source} modes[${index}].${k} must be a string`);
    }
  }
  if (typeof m.tempo !== 'number') {
    throw new Error(`loadModes: ${source} modes[${index}].tempo must be a number`);
  }
  if (!Array.isArray(m.oneshots) || !m.oneshots.every((o) => typeof o === 'string')) {
    throw new Error(`loadModes: ${source} modes[${index}].oneshots must be string[]`);
  }
  for (const block of ['feedback', 'chroma', 'scanline', 'post', 'gestureRanges'] as const) {
    if (!m[block] || typeof m[block] !== 'object') {
      throw new Error(`loadModes: ${source} modes[${index}].${block} must be an object`);
    }
  }
  const path = `loadModes: ${source} modes[${index}]`;
  const feedback = m.feedback as Record<string, unknown>;
  assertNumber(feedback.zoom, `${path}.feedback.zoom`);
  assertNumber(feedback.rotation, `${path}.feedback.rotation`);
  assertNumber(feedback.decay, `${path}.feedback.decay`);

  const chroma = m.chroma as Record<string, unknown>;
  assertNumber(chroma.splitMax, `${path}.chroma.splitMax`);
  assertNumber(chroma.angleHz, `${path}.chroma.angleHz`);

  const scanline = m.scanline as Record<string, unknown>;
  assertNumber(scanline.angleDeg, `${path}.scanline.angleDeg`);
  assertNumber(scanline.freq, `${path}.scanline.freq`);
  assertNumber(scanline.softness, `${path}.scanline.softness`);

  const post = m.post as Record<string, unknown>;
  assertNumber(post.barrel, `${path}.post.barrel`);
  assertNumber(post.bloomThreshold, `${path}.post.bloomThreshold`);
  assertNumber(post.vignette, `${path}.post.vignette`);

  const gestureRanges = m.gestureRanges as Record<string, unknown>;
  assertAscendingTuple(gestureRanges.dragXFilter, `${path}.gestureRanges.dragXFilter`);
  assertAscendingTuple(gestureRanges.dragYPitch, `${path}.gestureRanges.dragYPitch`);
}

function assertNumber(value: unknown, path: string): asserts value is number {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    throw new Error(`${path} must be a finite number`);
  }
}

function assertAscendingTuple(value: unknown, path: string): asserts value is [number, number] {
  if (
    !Array.isArray(value) ||
    value.length !== 2 ||
    typeof value[0] !== 'number' ||
    typeof value[1] !== 'number' ||
    !Number.isFinite(value[0]) ||
    !Number.isFinite(value[1]) ||
    value[0] >= value[1]
  ) {
    throw new Error(`${path} must be an ascending number tuple`);
  }
}
